import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Leaf, UtensilsCrossed } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import AdminLayout from "@/components/AdminLayout";
import api, { getMenuItems, createMenuItem, updateMenuItem, deleteMenuItem, getProperties } from "@/lib/api";
import { getCurrentUser } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const defaultFormData = {
  name: "",
  description: "",
  category: "breakfast",
  categories: [],
  price: "",
  image_url: "",
  is_available: true,
  is_vegetarian: false,
  property_id: "",
};

export function AdminMenuContent({ hideHeader = false }) {
  const [menuItems, setMenuItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [submitting, setSubmitting] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const [propertySettings, setPropertySettings] = useState(null);
  const [categorySettings, setCategorySettings] = useState([]);
  const [togglingFood, setTogglingFood] = useState(false);
  const [togglingCat, setTogglingCat] = useState({});
  const [togglingItem, setTogglingItem] = useState({});

  const categories = ["breakfast", "lunch", "dinner", "snacks", "beverages", "desserts", "todays_special", "combo"];
  const categoryIcons = {
    breakfast: "🌅",
    lunch: "☀️",
    dinner: "🌙",
    snacks: "🍿",
    beverages: "☕",
    desserts: "🍰",
    todays_special: "⭐",
    combo: "🍱",
  };

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchMenuItems();
      fetchCategoryAndSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  const fetchCategoryAndSettings = async () => {
    if (!selectedProperty) return;
    try {
      const [settingsRes, catRes] = await Promise.all([
        api.get(`/settings/${selectedProperty}`).catch(() => ({ data: null })),
        api.get(`/category-settings/${selectedProperty}`).catch(() => ({ data: [] })),
      ]);
      if (settingsRes?.data) setPropertySettings(settingsRes.data);
      if (Array.isArray(catRes?.data)) setCategorySettings(catRes.data);
    } catch (e) {
      // Non-blocking
    }
  };

  const handleToggleFood = async () => {
    if (!selectedProperty) return;
    setTogglingFood(true);
    try {
      const res = await api.post(`/settings/${selectedProperty}/toggle-food`);
      const newVal = res.data.food_enabled;
      setPropertySettings(prev => ({ ...(prev || {}), food_enabled: newVal }));
      setCategorySettings(prev => prev.map(c => ({ ...c, is_enabled: newVal, is_active: newVal })));
      toast.success(newVal ? "Restaurant is now OPEN 🟢" : "Restaurant is now CLOSED 🔴");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle restaurant");
    } finally {
      setTogglingFood(false);
    }
  };

  const handleToggleCategory = async (catKey) => {
    if (!selectedProperty) return;
    setTogglingCat(prev => ({ ...prev, [catKey]: true }));
    try {
      const res = await api.post(`/category-settings/${selectedProperty}/${catKey}/toggle`);
      const isEnabled = res.data.is_enabled;
      setCategorySettings(prev => {
        const exists = prev.some(c => c.category === catKey);
        if (exists) {
          return prev.map(c => c.category === catKey ? { ...c, is_enabled: isEnabled, is_active: isEnabled } : c);
        } else {
          return [...prev, { category: catKey, is_enabled: isEnabled, is_active: isEnabled, property_id: selectedProperty }];
        }
      });
      toast.success(`${catKey.charAt(0).toUpperCase() + catKey.slice(1)} is now ${isEnabled ? "OPEN 🟢" : "CLOSED 🔴"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to toggle ${catKey}`);
    } finally {
      setTogglingCat(prev => ({ ...prev, [catKey]: false }));
    }
  };

  const handleToggleItemAvailability = async (itemId) => {
    setTogglingItem(prev => ({ ...prev, [itemId]: true }));
    try {
      const res = await api.post(`/menu/${itemId}/toggle-availability`);
      const newAvail = res.data.is_available;
      setMenuItems(prev => prev.map(item => item.id === itemId ? { ...item, is_available: newAvail } : item));
      toast.success(`Dish marked as ${newAvail ? "Available 🟢" : "Out of Stock 🔴"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update item availability");
    } finally {
      setTogglingItem(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const fetchProperties = async () => {
    try {
      const data = await getProperties();
      const currentUser = getCurrentUser();
      if (currentUser?.role === "manager" && currentUser?.property_id) {
        const filtered = data.filter(p => p.property_id === currentUser.property_id);
        setProperties(filtered.length > 0 ? filtered : data);
        setSelectedProperty(currentUser.property_id);
      } else {
        setProperties(data);
        if (data.length > 0) {
          const defaultProp = (currentUser?.property_id && data.some(p => p.property_id === currentUser.property_id))
            ? currentUser.property_id
            : data[0].property_id;
          setSelectedProperty(defaultProp);
        }
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const data = await getMenuItems(selectedProperty, null, false);
      setMenuItems(data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
      toast.error("Failed to load menu items");
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = activeCategory === "all"
    ? menuItems
    : menuItems.filter((item) => {
        if (item.category === activeCategory) return true;
        if (Array.isArray(item.categories) && item.categories.includes(activeCategory)) return true;
        return false;
      });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        property_id: selectedProperty,
      };
      
      if (!submitData.image_url) {
        submitData.image_url = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=800"; // Generic food placeholder
      }

      if (editingId) {
        await updateMenuItem(editingId, submitData);
        toast.success("Menu item updated successfully");
      } else {
        await createMenuItem(submitData);
        toast.success("Menu item created successfully");
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...defaultFormData, property_id: selectedProperty });
      fetchMenuItems();
    } catch (error) {
      console.error("Error saving menu item:", error);
      toast.error("Failed to save menu item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    // Ensure categories array includes at least the primary category
    const categories = item.categories && item.categories.length > 0 
      ? item.categories 
      : [item.category];
    
    setFormData({
      name: item.name,
      description: item.description,
      category: item.category,
      categories: categories,
      price: item.price.toString(),
      image_url: item.image_url || "",
      is_available: item.is_available,
      is_vegetarian: item.is_vegetarian,
      property_id: item.property_id || selectedProperty,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await deleteMenuItem(deleteId);
      toast.success("Menu item deleted successfully");
      fetchMenuItems();
    } catch (error) {
      console.error("Error deleting menu item:", error);
      toast.error("Failed to delete menu item");
    } finally {
      setDeleteId(null);
    }
  };

  const openNewForm = () => {
    setFormData({ ...defaultFormData, property_id: selectedProperty, categories: ["breakfast"] });
    setEditingId(null);
    setShowForm(true);
  };

  return (
    <>
      <div data-testid="admin-menu">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-[#264653] mb-2">Menu Items</h1>
            <p className="text-[#6B705C]">Manage your food and beverages menu</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Property Selector */}
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-48 rounded-xl border-[#E8E4D9]" data-testid="menu-property-selector">
                <SelectValue placeholder="Select Property" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.property_id} value={p.property_id}>
                    {p.property_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={openNewForm} className="btn-primary flex items-center gap-2" data-testid="add-menu-item" disabled={!selectedProperty}>
              <Plus className="w-5 h-5" />
              Add Item
            </Button>
          </div>
        </div>

        {/* Restaurant Master & Category Controls */}
        <div className="space-y-4 mb-6">
          {/* Dedicated Restaurant Master Switch Hero Card */}
          <div
            className={`rounded-2xl p-4 sm:p-5 border transition-all shadow-sm ${
              propertySettings?.food_enabled !== false
                ? "bg-white border-emerald-200/90 ring-1 ring-emerald-500/10"
                : "bg-slate-50 border-rose-200/90 ring-1 ring-rose-500/10"
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start sm:items-center gap-3.5">
                <div
                  className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors ${
                    propertySettings?.food_enabled !== false ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                  }`}
                >
                  <UtensilsCrossed className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-bold text-base text-[#264653]">Restaurant Master Switch</h2>
                    <span
                      className={`text-xs font-bold px-2.5 py-0.5 rounded-full inline-flex items-center gap-1.5 transition-colors ${
                        propertySettings?.food_enabled !== false
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          propertySettings?.food_enabled !== false ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                        }`}
                      />
                      {propertySettings?.food_enabled !== false ? "RESTAURANT OPEN" : "RESTAURANT CLOSED"}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {propertySettings?.food_enabled !== false
                      ? "Kitchen is accepting orders. Guests can view the menu and place live food orders."
                      : "Kitchen is paused. Guests will see the restaurant as closed and cannot place orders."}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100">
                <span className="text-xs font-semibold text-gray-600 sm:hidden">
                  {propertySettings?.food_enabled !== false ? "Kitchen Accepting Orders" : "Kitchen Paused"}
                </span>
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-medium text-gray-500 hidden sm:inline">
                    {propertySettings?.food_enabled !== false ? "Open" : "Closed"}
                  </span>
                  <Switch
                    checked={propertySettings?.food_enabled !== false}
                    disabled={togglingFood || !selectedProperty}
                    onCheckedChange={handleToggleFood}
                    className="data-[state=checked]:bg-[#2A9D8F] scale-110 cursor-pointer"
                    data-testid="toggle-restaurant-master-admin"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Meal Category Quick Toggles */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 shadow-sm">
            <div className="mb-3.5">
              <h3 className="font-bold text-[#264653] text-sm sm:text-base flex items-center gap-2">
                <UtensilsCrossed className="w-4 h-4 text-[#2A9D8F]" />
                <span>Meal Category Quick Toggles</span>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Switch specific meal categories ON/OFF instantly without affecting the rest of the menu.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2.5">
              {categories.map((catKey) => {
                const setting = categorySettings.find(c => c.category === catKey);
                const isOpen = setting ? (setting.is_enabled !== false && setting.is_active !== false) : true;
                const isToggling = togglingCat[catKey];

                return (
                  <div
                    key={catKey}
                    onClick={() => !isToggling && selectedProperty && handleToggleCategory(catKey)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer select-none flex flex-col justify-between gap-2.5 ${
                      isOpen
                        ? "bg-white border-slate-200/90 hover:border-emerald-300 shadow-xs"
                        : "bg-slate-50/90 border-slate-200/70 opacity-75 hover:opacity-100"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-semibold text-xs text-[#264653] flex items-center gap-1.5 truncate">
                        <span>{categoryIcons[catKey] || "🍽️"}</span>
                        <span className="capitalize">{catKey === "todays_special" ? "Special" : catKey}</span>
                      </span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                          isOpen
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-500 border border-slate-200"
                        }`}
                      >
                        {isOpen ? "OPEN" : "OFF"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                      <span className="text-[11px] text-gray-500 font-medium">
                        {isOpen ? "Active" : "Paused"}
                      </span>
                      <Switch
                        checked={isOpen}
                        disabled={isToggling || !selectedProperty}
                        onCheckedChange={() => handleToggleCategory(catKey)}
                        className="data-[state=checked]:bg-[#2A9D8F]"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Categories Filter */}
        <div className="flex gap-3 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveCategory("all")}
            className={`category-pill whitespace-nowrap ${activeCategory === "all" ? "active" : ""}`}
            data-testid="filter-all"
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`category-pill whitespace-nowrap capitalize ${activeCategory === cat ? "active" : ""}`}
              data-testid={`filter-${cat}`}
            >
              {cat === "todays_special" ? "Today's Special" : cat}
            </button>
          ))}
        </div>

        {/* Menu Items Table */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-[#E8E4D9] overflow-hidden overflow-x-auto">
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-16 h-16 rounded-xl" />
                  <div className="flex-1">
                    <Skeleton className="h-5 w-1/3 mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E4D9]">
            <p className="text-[#6B705C] mb-4">No menu items found</p>
            <Button onClick={openNewForm} className="btn-primary">
              Add Your First Item
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E8E4D9] overflow-hidden overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} data-testid={`menu-item-row-${item.id}`}>
                    <td>
                      <div className="flex items-center gap-4">
                        {item.image_url ? (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-12 h-12 rounded-xl object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-[#F0EFEB] flex items-center justify-center">
                            <span className="text-[#6B705C] text-lg">🍽️</span>
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-[#264653] flex items-center gap-1">
                            {item.name}
                            {item.is_vegetarian && <Leaf className="w-4 h-4 text-green-600" />}
                          </p>
                          <p className="text-[#6B705C] text-sm line-clamp-1">{item.description}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="capitalize text-[#264653]">{item.category}</span>
                    </td>
                    <td>
                      <span className="font-bold text-[#2A9D8F]">₹{item.price}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => handleToggleItemAvailability(item.id)}
                        disabled={togglingItem[item.id]}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full border transition-all ${
                          item.is_available
                            ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                            : "bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                        }`}
                        title="Click to toggle In Stock / Out of Stock"
                        data-testid={`quick-toggle-${item.id}`}
                      >
                        {item.is_available ? "In Stock 🟢" : "Out of Stock 🔴"}
                      </button>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-[#6B705C] hover:bg-[#F0EFEB] transition-colors"
                          data-testid={`edit-menu-item-${item.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteId(item.id)}
                          className="w-9 h-9 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" aria-label="Delete menu item" title="Delete item"
                          data-testid={`delete-menu-item-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Form Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="sm:max-w-lg bg-white rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#264653]">
                {editingId ? "Edit Menu Item" : "Add Menu Item"}
              </DialogTitle>
              <DialogDescription className="text-[#6B705C]">
                {editingId ? "Update the menu item details" : "Add a new item to your menu"}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="menu-item-form">
              <div>
                <Label htmlFor="name" className="text-[#264653]">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="hm-input mt-1"
                  placeholder="Masala Chai"
                  required
                  data-testid="menu-name-input"
                />
              </div>

              <div>
                <Label htmlFor="category" className="text-[#264653]">Primary Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => {
                    const newCategories = formData.categories.includes(value) 
                      ? formData.categories 
                      : [...formData.categories, value];
                    setFormData({ ...formData, category: value, categories: newCategories });
                  }}
                >
                  <SelectTrigger className="hm-input mt-1" data-testid="menu-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat} className="capitalize">
                        {cat === "todays_special" ? "Today's Special" : cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#264653]">Additional Categories (Multi-select)</Label>
                <p className="text-xs text-[#6B705C] mb-2">Item will show when any selected category is active</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {categories.map((cat) => (
                    <label
                      key={cat}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        formData.categories.includes(cat)
                          ? "bg-[#2A9D8F] text-white border-[#2A9D8F]"
                          : "bg-white text-[#264653] border-[#E8E4D9] hover:border-[#2A9D8F]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.categories.includes(cat)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, categories: [...formData.categories, cat] });
                          } else {
                            // Don't allow unchecking primary category
                            if (cat === formData.category) return;
                            setFormData({ 
                              ...formData, 
                              categories: formData.categories.filter(c => c !== cat) 
                            });
                          }
                        }}
                        className="sr-only"
                        data-testid={`category-checkbox-${cat}`}
                      />
                      <span className="capitalize text-sm">{cat === "todays_special" ? "Today's Special" : cat}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="description" className="text-[#264653]">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="hm-input mt-1 min-h-[80px]"
                  placeholder="Traditional spiced tea..."
                  required
                  data-testid="menu-description-input"
                />
              </div>

              <div>
                <Label htmlFor="price" className="text-[#264653]">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="hm-input mt-1"
                  placeholder="50"
                  required
                  data-testid="menu-price-input"
                />
              </div>

              <div>
                <Label htmlFor="image_url" className="text-[#264653]">Image URL (optional)</Label>
                <div className="mt-1">
                  <ImageUpload 
                    value={formData.image_url} 
                    onChange={(url) => setFormData({ ...formData, image_url: url })} 
                  />
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <Label htmlFor="is_vegetarian" className="text-[#264653]">Vegetarian</Label>
                <Switch
                  id="is_vegetarian"
                  checked={formData.is_vegetarian}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_vegetarian: checked })}
                  data-testid="menu-vegetarian-switch"
                />
              </div>

              <div className="flex items-center justify-between py-2">
                <Label htmlFor="is_available" className="text-[#264653]">Available</Label>
                <Switch
                  id="is_available"
                  checked={formData.is_available}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                  data-testid="menu-available-switch"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={submitting}
                data-testid="save-menu-item"
              >
                {submitting ? "Saving..." : (editingId ? "Update Item" : "Add Item")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent className="bg-white rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-xl text-[#264653]">
                Delete Menu Item?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#6B705C]">
                This action cannot be undone. This will permanently delete the menu item.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="btn-destructive"
                data-testid="confirm-delete-menu"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}

export default function AdminMenu() {
  return (
    <AdminLayout>
      <AdminMenuContent />
    </AdminLayout>
  );
}
